import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Platform, SocialPlatform } from '../../types/api';

interface OAuthState {
  platform: SocialPlatform | null;
  loading: boolean;
  error: string | null;
}

interface IntegrationsState {
  connectedPlatforms: Platform[];
  oauthState: OAuthState;
  loading: boolean;
  error: string | null;
}

const initialState: IntegrationsState = {
  connectedPlatforms: [],
  oauthState: {
    platform: null,
    loading: false,
    error: null,
  },
  loading: false,
  error: null,
};

const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    setConnectedPlatforms: (state, action: PayloadAction<Platform[]>) => {
      state.connectedPlatforms = action.payload;
    },
    addConnectedPlatform: (state, action: PayloadAction<Platform>) => {
      const existingIndex = state.connectedPlatforms.findIndex((p: Platform) => p.name === action.payload.name);
      if (existingIndex >= 0) {
        state.connectedPlatforms[existingIndex] = action.payload;
      } else {
        state.connectedPlatforms.push(action.payload);
      }
    },
    removeConnectedPlatform: (state, action: PayloadAction<SocialPlatform>) => {
      state.connectedPlatforms = state.connectedPlatforms.filter((p: Platform) => p.name !== action.payload);
    },
    updatePlatformConnection: (state, action: PayloadAction<{ platform: SocialPlatform; connected: boolean }>) => {
      const platform = state.connectedPlatforms.find((p: Platform) => p.name === action.payload.platform);
      if (platform) {
        platform.isConnected = action.payload.connected;
      }
    },
    setOAuthState: (state, action: PayloadAction<Partial<OAuthState>>) => {
      state.oauthState = { ...state.oauthState, ...action.payload };
    },
    clearOAuthState: (state) => {
      state.oauthState = initialState.oauthState;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setConnectedPlatforms,
  addConnectedPlatform,
  removeConnectedPlatform,
  updatePlatformConnection,
  setOAuthState,
  clearOAuthState,
  setLoading,
  setError,
  clearError,
} = integrationsSlice.actions;

export default integrationsSlice.reducer; 